import type { I18nLocale } from '@ricoh/ria.client.core/plugins/i18n';

import DateTime from '@ricoh/ria.client.core/plugins/moment';

/** App information. */
export const APP_INFO = (() => {
  const SHARED = {
    ID: '@ricoh/ria.client',
    NAME: 'RIA Client',
    VERSION: APP_BUILD_VERSION.version,
    ORGANIZATION: 'RICOH',
    LOGO: 'RICOH Intelligent Automation',
  } as const;

  return {
    ...SHARED,

    CORE: (() => {
      const CORE = {
        ID: `${SHARED.ID}.core`,
        NAME: `${SHARED.NAME} Core`,
      } as const;

      const STORAGE = {
        NAME: `${CORE.ID}/storage` as const,
        VERSION: 1,
        STORE: {
          I18N: {
            LOCALE: 'i18n.locale',
          },
        },
      } as const;

      const DATABASE = {
        NAME: `${CORE.ID}/database` as const,
        VERSION: 1,
        STORE: {
          AUTH: {
            CONFIGS: 'auth.configs',
          },
        },
      } as const;

      return {
        ...CORE,
        STORAGE,
        DATABASE,
      };
    })(),

    WEB: (() => {
      const WEB = {
        ID: `${SHARED.ID}.web`,
        NAME: `${SHARED.NAME} Web`,
      } as const;

      const STORAGE = {
        NAME: `${WEB.ID}/storage` as const,
        VERSION: 1,
        STORE: {
          PROCESS: {
            SOURCE_SETTINGS: {
              SCANNER: 'process.sourceSettings.scanner',
            },
          },
          WORK_QUEUE: {
            RECENT_FILTERS: 'workQueue.recentFilters',
          },
          COOKIE_PROMPT: {
            SETTINGS: 'settings.cookiePrompt',
          },
          CHATBOT: {
            WELCOME_MESSAGE_SHOWN: 'chatbot.welcomeMessageShown',
          },
        },
      } as const;

      const DATABASE = {
        NAME: `${WEB.ID}/database` as const,
        VERSION: 1,
        STORE: {
          PROCESS: {
            SESSIONS: 'process.sessions',
            CHILD_PROCESSES: 'process.childProcesses',
          },
        },
      } as const;

      const WORKER = {
        ID: `${WEB.ID}.worker`,
        NAME: `${WEB.NAME} Worker`,
      } as const;

      return {
        ...WEB,
        STORAGE,
        DATABASE,
        WORKER,
      } as const;
    })(),

    ADMIN: (() => {
      const ADMIN = {
        ID: `${SHARED.ID}.admin`,
        NAME: `${SHARED.NAME} Admin`,
      } as const;

      const WORKER = {
        ID: `${ADMIN.ID}.worker`,
        NAME: `${ADMIN.NAME} Worker`,
      } as const;

      return {
        ...ADMIN,
        WORKER,
      } as const;
    })(),
  } as const;
})();

/** App locale name. */
export const APP_LOCALE_NAME: Record<I18nLocale, string> = {
  ca: 'Català', // Catalan
  nl: 'Nederlands', // Dutch
  en: 'English', // English
  fr: 'Français', // French
  de: 'Deutsch', // German
  it: 'Italiano', // Italian
  ja: '日本語', // Japanese
  pt: 'Português', // Portuguese
  es: 'Español', // Spanish
  zh: '繁體中文', // Traditional Chinese
  th: 'ไทย', // Thai
  vi: 'Tiếng Việt', // Vietnamese
};

/** App locales. */
export const APP_LOCALES = Object.keys<I18nLocale>(APP_LOCALE_NAME);

/** App locale name fallback. */
export const APP_LOCALE_NAME_FALLBACK: I18nLocale = 'en';

/** App locale name default. */
export const APP_LOCALE_NAME_DEFAULT: I18nLocale = (() => {
  const [browserLocale] = navigator.language.split('-');
  return browserLocale && APP_LOCALES.includes(browserLocale) ? browserLocale : APP_LOCALE_NAME_FALLBACK;
})();

/** App External url. */
export const APP_EXTERNAL_URL = {
  X_TWITTER: 'https://x.com/RicohTweets',
  YOUTUBE: 'https://www.youtube.com/@RicohUSAInc',
  IDEAS_FEEDBACK: 'https://ricoh-ia.ideas.aha.io/ideas/new',
  POWER_BI: 'https://app.powerbi.com/',
  RICOH_FI: 'https://www.pfu.ricoh.com/global/scanners/fi/dl/',
  USER_HELP_DOCUMENTATION_LINK: 'https://docs.ria.ricoh.com/ricohia/Home.htm',
  ADMIN_HELP_DOCUMENTATION_LINK: 'https://docs.ria.ricoh.com/operations-ricohia/Content/Home.htm',
} as const;

/** App content last updated. */
export const CONTENT_LAST_UPDATED = {
  PRIVACY_POLICY: DateTime.nullablePattern({ date: '2025/07/05' }) ?? DateTime(),
  COOKIE_POLICY: DateTime.nullablePattern({ date: '2025/11/07' }) ?? DateTime(),
  IMPRINT: DateTime.nullablePattern({ date: '2025/06/30' }) ?? DateTime(),
} as const;
